import { QueryErrorBoundary } from "@components/common/errors";
import { TableSkeleton } from "@components/common/skeletons";
import { DataTable } from "@components/ui/data-table/DataTable";
import { BulkActionButton, BulkActionsBar } from "@features/shared/bulk-actions";
import { CsvExportModal } from "@features/shared/csv-export";
import api from "@lib/api";
import { useBulkActions } from "@lib/hooks/useBulkActions";
import { useDebounce } from "@lib/hooks/useDebounce";
import { useTable } from "@lib/hooks/useTable";
import { useTableUISettings } from "@lib/hooks/useTableUISettings";
import { Link, useNavigate } from "@tanstack/react-router";
import { Download, Lock, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getTicketColumns } from "../../config";
import { API_ENDPOINTS, ROUTES } from "../../config/constants";
import { useTicketSubscription } from "../../hooks/useTicketSubscription";
import { useTicketsFilters } from "../../hooks/useTicketsFilters";
import { useTicketsMetrics } from "../../hooks/useTicketsMetrics";
import type { Ticket } from "../../types/ticket.types";
import { TicketCategoryBadge } from "../shared/TicketCategoryBadge";
import { TicketStatusBadge } from "../shared/TicketStatusBadge";
import { TicketsMetricsCards } from "../shared/TicketsMetricsCards";
import { TicketsActiveFilters } from "./TicketsActiveFilters";
import { TicketsQuickFilters } from "./TicketsQuickFilters";
import { TicketsToolbar } from "./TicketsToolbar";

export function TicketsTab() {
  const { data: metricsData, isLoading: metricsLoading, error: metricsError } = useTicketsMetrics();
  useTicketSubscription();
  const uiSettings = useTableUISettings();
  const filters = useTicketsFilters();
  const navigate = useNavigate();

  const debouncedSearch = useDebounce(filters.searchQuery, 300);

  const columns = useMemo(() => getTicketColumns(uiSettings), [uiSettings]);

  const {
    table,
    data,
    isLoading: tableLoading,
    isFetching,
    error: tableError,
    refetch,
    totalItems,
    selectedRows,
    clearSelection,
  } = useTable<Ticket>({
    resource: API_ENDPOINTS.TICKETS.LIST,
    mercureTopic: API_ENDPOINTS.TICKETS.MERCURE_TOPIC,
    columns,
    serverFilters: filters.apiFilters,
    mercureEnabled: true,
    defaultPageSize: 25,
    enableRowSelection: true,
    onError: "toast",
  });

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  useEffect(() => {
    table.setGlobalFilter(debouncedSearch);
  }, [debouncedSearch, table]);

  const handleRowClick = (row: Ticket, e: React.MouseEvent<HTMLTableRowElement>) => {
    if ((e.target as HTMLElement).closest("a,button,input,label")) return;
    navigate({ to: ROUTES.TICKETS.VIEW, params: { id: String(row.id) } });
  };

  const { executeAction, isProcessing } = useBulkActions({
    resource: API_ENDPOINTS.TICKETS.LIST,
    queryKey: "tickets",
    onSuccess: () => {
      clearSelection();
      refetch();
    },
    onError: () => {},
  });

  const handleBulkClose = () => {
    executeAction(selectedRows, {
      type: "custom",
      customFn: (item) => api.post(API_ENDPOINTS.TICKETS.CLOSE(item.id)),
      successMessage: `${selectedRows.length} ticket(s) fermé(s)`,
    });
  };

  const handleClearFilters = () => {
    filters.clearFilters();
    table.resetColumnFilters();
    table.setGlobalFilter("");
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center animate-fade-in">
        <div>
          <div className="flex flex-wrap items-baseline gap-2.5">
            <h1 className="font-display text-xl font-bold text-[var(--ink)]">Tickets</h1>
            <span className="rounded-[var(--radius-selector)] border border-[var(--line)] bg-[var(--surface-sunken)] px-2 py-0.5 font-mono text-[11px] text-[var(--ink-faint)]">
              MOD-SUPPORT
            </span>
          </div>
          <p className="text-sm text-base-content/60 mt-1">
            {totalItems} ticket{totalItems > 1 ? "s" : ""} au total
          </p>
          <p className="max-w-[520px] text-[12.5px] leading-relaxed text-[var(--ink-soft)]">
            Suivi des anomalies, demandes d'évolution et signalements de l'ensemble des
            collaborateurs.
          </p>
        </div>
        <Link to={ROUTES.TICKETS.CREATE} className="btn btn-primary gap-2">
          <Plus className="w-4 h-4" /> Nouveau ticket
        </Link>
      </div>

      {/* Metrics */}
      <div
        className="animate-fade-in"
        style={{ animationDelay: "200ms", animationFillMode: "both" }}
      >
        <TicketsMetricsCards
          isLoading={metricsLoading}
          isError={Boolean(metricsError)}
          data={metricsData}
        />
      </div>

      {/* Toolbar */}
      <div
        className="animate-fade-in"
        style={{ animationDelay: "250ms", animationFillMode: "both" }}
      >
        <TicketsToolbar filters={filters} onRefresh={refetch} isRefreshing={isFetching} />
      </div>

      {/* Quick filters */}
      <div
        className="animate-fade-in"
        style={{ animationDelay: "300ms", animationFillMode: "both" }}
      >
        <TicketsQuickFilters
          active={filters.activeQuickFilter}
          onChange={filters.setActiveQuickFilter}
        />
      </div>

      {/* Active filters */}
      <div
        className="animate-fade-in"
        style={{ animationDelay: "350ms", animationFillMode: "both" }}
      >
        <TicketsActiveFilters filters={filters} />
      </div>

      {tableLoading && <TableSkeleton rows={10} columns={6} hasHeader />}
      {tableError && <QueryErrorBoundary error={tableError} refetch={refetch} />}
      {!tableLoading && !tableError && (
        <>
          <span className="sr-only" aria-live="polite">
            {totalItems} résultat{totalItems > 1 ? "s" : ""}
          </span>
          <DataTable<Ticket>
            table={table}
            columns={columns}
            onRowClick={(row, e) => handleRowClick(row.original, e)}
            renderEmpty={() => (
              <div
                role="status"
                aria-live="polite"
                className="flex flex-col items-center gap-3 animate-fade-in"
              >
                <p className="text-lg font-medium">Aucun ticket trouvé</p>
                <p className="text-sm text-base-content/50">
                  Créez votre premier ticket pour signaler un problème ou demander une amélioration.
                </p>
                <Link to={ROUTES.TICKETS.CREATE} className="btn btn-primary btn-sm gap-2">
                  <Plus className="w-4 h-4" /> Créer un ticket
                </Link>
              </div>
            )}
            mobileCard={{
              renderCard: (row) => (
                <div className="card bg-base-100 border border-base-300/70 shadow-sm p-3 space-y-2">
                  <span className="font-mono text-[11px] text-base-content/50">
                    TK-{String(row.original.id).slice(0, 8).toUpperCase()}
                  </span>
                  <Link
                    to={ROUTES.TICKETS.VIEW}
                    params={{ id: String(row.original.id) }}
                    className="font-semibold text-sm"
                  >
                    {row.original.title}
                  </Link>
                  <div className="flex items-center gap-2">
                    <TicketStatusBadge status={row.original.status} />
                    <TicketCategoryBadge category={row.original.category} />
                  </div>
                </div>
              ),
            }}
          />
        </>
      )}

      {/* Bulk Actions Bar */}
      <BulkActionsBar selectedCount={selectedRows.length} onClear={clearSelection} className="">
        <BulkActionButton
          icon={Lock}
          label="Fermer"
          variant="success"
          onClick={handleBulkClose}
          confirmMessage={`Fermer ${selectedRows.length} ticket(s) ?`}
          disabled={isProcessing}
          isLoading={isProcessing}
        />
        <BulkActionButton
          icon={Download}
          label="Exporter CSV"
          variant="info"
          onClick={() => setIsExportModalOpen(true)}
          confirmMessage=""
          disabled={false}
          isLoading={false}
        />
      </BulkActionsBar>

      {/* CSV Export Modal */}
      <CsvExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        data={data}
        columns={columns}
        filename="tickets.csv"
        selectedRows={selectedRows.length > 0 ? selectedRows : undefined}
      />
    </div>
  );
}
